import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { importReferential, type ImportResult } from "../api/referentialsApi";

type Props = {
  kind: "acts" | "icd10";
  /** Colonnes attendues, affichées en aide. */
  columns: string;
  onImported: () => void;
};

/**
 * Mise à jour d'un référentiel par fichier CSV (Excel : Enregistrer sous → CSV).
 * Les codes existants sont mis à jour, les nouveaux ajoutés ; les lignes en erreur sont listées.
 */
export function ReferentialImport({ kind, columns, onImported }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await importReferential(kind, file));
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import impossible.");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => input.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-60"
        >
          <Upload size={14} />
          {busy ? "Import en cours…" : "Importer un fichier CSV"}
        </button>
        <span className="text-xs text-muted">Colonnes : {columns}. Séparateur « ; » ou « , ».</span>
        <input ref={input} type="file" accept=".csv,.txt" className="hidden" onChange={(e) => void handleFile(e.target.files?.[0])} />
      </div>
      {error ? <p className="mt-2 text-red-600">{error}</p> : null}
      {result ? (
        <div className="mt-2">
          <p className="text-foreground">
            {result.created} code(s) ajouté(s), {result.updated} mis à jour
            {result.errors.length > 0 ? `, ${result.errors.length} ligne(s) ignorée(s)` : ""}.
          </p>
          {result.errors.length > 0 ? (
            <ul className="mt-1 max-h-32 list-disc overflow-y-auto pl-5 text-xs text-amber-700">
              {result.errors.slice(0, 50).map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
